import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient, getPipelineSummary } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Edit, Trash2, Eye, Search, Users, UserCheck, UserX, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const clientStatuses = {
  potential: { label: 'Potenciální', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Users },
  active: { label: 'Aktivní', color: 'bg-green-100 text-green-800 border-green-300', icon: UserCheck },
  churned: { label: 'Bývalý', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: UserX },
};

const Clients = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [clients, setClients] = useState([]);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    status: 'potential',
    contact_email: '',
    contact_phone: '',
    contact_person: '',
    notes: '',
  });

  const fetchClients = async () => {
    try {
      const [clientsRes, pipelineRes] = await Promise.all([
        getClients(),
        getPipelineSummary(),
      ]);
      setClients(clientsRes.data);
      setPipelineSummary(pipelineRes.data);
    } catch (error) {
      toast.error('Chyba při načítání klientů');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Klient aktualizován');
      } else {
        await createClient(formData);
        toast.success('Klient vytvořen');
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData({ name: '', industry: '', status: 'potential', contact_email: '', contact_phone: '', contact_person: '', notes: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      industry: client.industry,
      status: client.status || 'potential',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      contact_person: client.contact_person || '',
      notes: client.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tohoto klienta?')) return;
    try {
      await deleteClient(id);
      toast.success('Klient smazán');
      fetchClients();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
            Klienti
          </h1>
          <p className="font-serif text-gray-600 mt-1">
            Správa klientů a obchodní pipeline
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              onClick={() => {
                setEditingClient(null);
                setFormData({ name: '', industry: '', status: 'potential', contact_email: '', contact_phone: '', contact_person: '', notes: '' });
              }}
              data-testid="add-client-btn"
            >
              <Plus size={18} className="mr-2" />
              Nový klient
            </Button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                {editingClient ? 'Upravit klienta' : 'Nový klient'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Název firmy *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Obor *</Label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    required
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="client-industry-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Status *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="client-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="potential">Potenciální klient</SelectItem>
                      <SelectItem value="active">Aktivní klient</SelectItem>
                      <SelectItem value="churned">Bývalý klient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Kontaktní osoba</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="client-contact-person-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Telefon</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Poznámky</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="client-notes-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                data-testid="client-submit-btn"
              >
                {editingClient ? 'Uložit změny' : 'Vytvořit klienta'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Summary */}
      {pipelineSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Potenciální klienti */}
          <div
            className={`bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all ${filterStatus === 'potential' ? 'ring-2 ring-[#006CB6]' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'potential' ? 'all' : 'potential')}
            data-testid="pipeline-potential"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">Potenciální</div>
                <div className="font-oswald text-3xl font-bold">{pipelineSummary.potential?.count || 0}</div>
                {isAdmin && (
                  <div className="font-serif text-sm text-gray-600 mt-1">
                    Odhad: {formatCurrency(pipelineSummary.potential?.estimated_revenue || 0)}
                  </div>
                )}
              </div>
              <div className="p-3 border-2 border-black bg-yellow-100 text-yellow-800">
                <Users size={24} />
              </div>
            </div>
          </div>
          
          {/* Aktivní klienti */}
          <div
            className={`bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all ${filterStatus === 'active' ? 'ring-2 ring-[#006CB6]' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'active' ? 'all' : 'active')}
            data-testid="pipeline-active"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">Aktivní</div>
                <div className="font-oswald text-3xl font-bold">{pipelineSummary.active?.count || 0}</div>
                {isAdmin && (
                  <>
                    <div className="font-serif text-sm text-gray-600 mt-1">
                      Odhad: {formatCurrency(pipelineSummary.active?.estimated_revenue || 0)}
                    </div>
                    <div className="font-serif text-sm text-green-600">
                      Realita: {formatCurrency(pipelineSummary.active?.actual_revenue || 0)}
                    </div>
                  </>
                )}
              </div>
              <div className="p-3 border-2 border-black bg-green-100 text-green-800">
                <UserCheck size={24} />
              </div>
            </div>
          </div>
          
          {/* Bývalí klienti */}
          <div
            className={`bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all ${filterStatus === 'churned' ? 'ring-2 ring-[#006CB6]' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'churned' ? 'all' : 'churned')}
            data-testid="pipeline-churned"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">Bývalí</div>
                <div className="font-oswald text-3xl font-bold">{pipelineSummary.churned?.count || 0}</div>
                {isAdmin && (
                  <div className="font-serif text-sm text-gray-600 mt-1">
                    Realizováno: {formatCurrency(pipelineSummary.churned?.actual_revenue || 0)}
                  </div>
                )}
              </div>
              <div className="p-3 border-2 border-black bg-gray-100 text-gray-800">
                <UserX size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Hledat klienty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-2 border-gray-200 focus:border-black rounded-none font-serif"
            data-testid="search-clients"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-48 border-2 border-gray-200 rounded-none" data-testid="filter-status">
            <SelectValue placeholder="Filtr statusu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všechny stavy</SelectItem>
            <SelectItem value="potential">Potenciální</SelectItem>
            <SelectItem value="active">Aktivní</SelectItem>
            <SelectItem value="churned">Bývalí</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 border-b-2 border-black">
              <TableHead className="font-oswald uppercase text-xs tracking-wide">Název</TableHead>
              <TableHead className="font-oswald uppercase text-xs tracking-wide">Status</TableHead>
              <TableHead className="font-oswald uppercase text-xs tracking-wide hidden md:table-cell">Obor</TableHead>
              <TableHead className="font-oswald uppercase text-xs tracking-wide hidden lg:table-cell">Kontakt</TableHead>
              <TableHead className="font-oswald uppercase text-xs tracking-wide text-right">Akce</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center font-serif text-gray-500 py-8">
                  {searchTerm || filterStatus !== 'all' ? 'Žádní klienti nenalezeni' : 'Zatím nemáte žádné klienty'}
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => {
                const statusInfo = clientStatuses[client.status] || clientStatuses.potential;
                return (
                  <TableRow key={client.id} className="border-b border-gray-200">
                    <TableCell className="font-serif font-medium">
                      <Link to={`/clients/${client.id}`} className="hover:text-[#006CB6]">
                        {client.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs font-oswald uppercase border ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </TableCell>
                    <TableCell className="font-serif hidden md:table-cell">{client.industry}</TableCell>
                    <TableCell className="font-serif hidden lg:table-cell">
                      {client.contact_person || client.contact_email || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/clients/${client.id}`}
                          className="p-2 hover:bg-gray-100 border border-gray-200"
                          data-testid={`view-client-${client.id}`}
                        >
                          <Eye size={16} />
                        </Link>
                        <button
                          onClick={() => handleEdit(client)}
                          className="p-2 hover:bg-gray-100 border border-gray-200"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="p-2 hover:bg-red-50 border border-gray-200 text-red-600"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Clients;
